import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { loadOrgChartData } from './orgChartService';

/**
 * Ensure user document exists, create if it doesn't
 * @returns {Promise<Object>} User document data
 */
const ensureUserDocumentExists = async (userId = null) => {
  const auth = getAuth();
  const uid = userId || auth.currentUser?.uid;
  if (!uid) {
    throw new Error('User must be authenticated');
  }

  const userDocRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userDocRef);

  if (!userDoc.exists()) {
    console.log('⚠️ User document does not exist, creating basic user document...');

    // Create a basic user document
    const basicUserData = {
      uid,
      email: auth.currentUser?.email,
      firstName: auth.currentUser?.displayName?.split(' ')[0] || 'User',
      lastName: auth.currentUser?.displayName?.split(' ').slice(1).join(' ') || '',
      role: 'client',
      status: 'active',
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      userType: 'portal'
    };
    
    await setDoc(userDocRef, basicUserData);
    console.log('✅ Basic user document created');
    return basicUserData;
  }
  
  return userDoc.data();
};

/**
 * Load labor burden data for the current user
 * @returns {Promise<Object>} Labor burden data
 */
export const loadLaborBurdenData = async (userId = null) => {
  try {
    const auth = getAuth();
    const uid = userId || auth.currentUser?.uid;
    if (!uid) {
      throw new Error('User must be authenticated to load labor burden data');
    }

    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.laborBurden || {
        employees: [],
        workDays: 251,
        pto: 10,
        holidays: 7,
        hourlyRate: 30,
        hourlyExtraCosts: 15,
        unbillablePercentage: 10,
        salaryType: 'annual',
        salaryRate: 62400,
        salaryExtraCosts: 15,
        lastUpdated: null
      };
    }
    
    // Return default data if user document doesn't exist
    return {
      employees: [],
      workDays: 251,
      pto: 10,
      holidays: 7,
      hourlyRate: 30,
      hourlyExtraCosts: 15,
      unbillablePercentage: 10,
      salaryType: 'annual',
      salaryRate: 62400,
      salaryExtraCosts: 15,
      lastUpdated: null
    };
  } catch (error) {
    console.error('Error loading labor burden data:', error);
    throw error;
  }
};

/**
 * Save labor burden data for the current user
 * @param {Object} laborBurdenData - Labor burden data to save
 * @returns {Promise<Object>} Saved labor burden data
 */
export const saveLaborBurdenData = async (laborBurdenData, userId = null) => {
  try {
    const auth = getAuth();
    const uid = userId || auth.currentUser?.uid;
    if (!uid) {
      throw new Error('User must be authenticated to save labor burden data');
    }

    // Ensure user document exists
    const currentUserData = await ensureUserDocumentExists(uid);
    const userDocRef = doc(db, 'users', uid);

    // Prepare the labor burden data with metadata
    const laborBurdenDataWithMetadata = {
      employees: laborBurdenData.employees || [],
      workDays: laborBurdenData.workDays || 251,
      pto: laborBurdenData.pto || 10,
      holidays: laborBurdenData.holidays || 7,
      hourlyRate: laborBurdenData.hourlyRate || 30,
      hourlyExtraCosts: laborBurdenData.hourlyExtraCosts || 15,
      unbillablePercentage: laborBurdenData.unbillablePercentage || 10,
      salaryType: laborBurdenData.salaryType || 'annual',
      salaryRate: laborBurdenData.salaryRate || 62400,
      salaryExtraCosts: laborBurdenData.salaryExtraCosts || 15,
      lastUpdated: serverTimestamp(),
      version: (currentUserData.laborBurden?.version || 0) + 1
    };

    // Use setDoc with merge to handle both existing and non-existing documents
    await setDoc(userDocRef, {
      laborBurden: laborBurdenDataWithMetadata,
      lastUpdated: serverTimestamp()
    }, { merge: true });

    console.log('✅ Labor burden data saved successfully');
    return laborBurdenDataWithMetadata;
  } catch (error) {
    console.error('Error saving labor burden data:', error);
    throw error;
  }
};

/**
 * Generate labor burden employees from org chart data
 * @returns {Promise<Array>} Array of labor burden employees
 */
export const generateFromOrgChart = async (userId = null) => {
  try {
    const auth = getAuth();
    const uid = userId || auth.currentUser?.uid;
    if (!uid) {
      throw new Error('User must be authenticated to generate from org chart');
    }

    // Load org chart data. loadOrgChartData takes an accountId (not a userId),
    // so the override uid is not threaded into it — it reads the org chart of
    // whoever is resolved by that service.
    const orgChartData = await loadOrgChartData();
    const orgEmployees = orgChartData.employees || [];

    if (orgEmployees.length === 0) {
      throw new Error('No employees found in org chart. Please add employees to your org chart first.');
    }

    // Convert org chart employees to labor burden format
    const laborBurdenEmployees = orgEmployees.map(emp => {
      const salary = parseFloat(emp.salary?.replace(/[^0-9.-]+/g, "") || "50000");
      
      return {
        id: emp.id,
        name: emp.name,
        jobTitle: emp.title,
        type: 'salaried', // All employees from org chart are treated as salaried
        rate: salary, // Use original salary
        extraCosts: 15, // Default extra costs percentage
        unbillablePercentage: 0, // Salaried employees have 0% unbillable
        orgChartId: emp.id // Keep reference to org chart employee
      };
    });

    return laborBurdenEmployees;
  } catch (error) {
    console.error('Error generating from org chart:', error);
    throw error;
  }
};

/**
 * Update a specific employee in labor burden data
 * @param {string} employeeId - Employee ID
 * @param {Object} employeeData - Updated employee data
 * @returns {Promise<Array>} Updated employees array
 */
export const updateLaborBurdenEmployee = async (employeeId, employeeData, userId = null) => {
  try {
    const auth = getAuth();
    const uid = userId || auth.currentUser?.uid;
    if (!uid) {
      throw new Error('User must be authenticated to update labor burden employee');
    }

    // Ensure user document exists and get current data
    const userData = await ensureUserDocumentExists(uid);
    const currentLaborBurden = userData.laborBurden || { employees: [] };

    // Update the specific employee
    const updatedEmployees = currentLaborBurden.employees.map(emp =>
      emp.id === employeeId ? { ...emp, ...employeeData } : emp
    );

    // Save updated labor burden data
    await saveLaborBurdenData({
      ...currentLaborBurden,
      employees: updatedEmployees
    }, uid);

    return updatedEmployees;
  } catch (error) {
    console.error('Error updating labor burden employee:', error);
    throw error;
  }
};

/**
 * Add a new employee to labor burden data
 * @param {Object} employeeData - New employee data
 * @returns {Promise<Object>} New employee
 */
export const addLaborBurdenEmployee = async (employeeData, userId = null) => {
  try {
    const auth = getAuth();
    const uid = userId || auth.currentUser?.uid;
    if (!uid) {
      throw new Error('User must be authenticated to add labor burden employee');
    }

    // Ensure user document exists and get current data
    const userData = await ensureUserDocumentExists(uid);
    const currentLaborBurden = userData.laborBurden || { employees: [] };

    // Add new employee with unique ID
    const newEmployee = {
      ...employeeData,
      id: employeeData.id || Date.now().toString(),
      createdAt: new Date().toISOString()
    };

    const updatedEmployees = [...currentLaborBurden.employees, newEmployee];

    // Save updated labor burden data
    await saveLaborBurdenData({
      ...currentLaborBurden,
      employees: updatedEmployees
    }, uid);

    return newEmployee;
  } catch (error) {
    console.error('Error adding labor burden employee:', error);
    throw error;
  }
};

/**
 * Remove an employee from labor burden data
 * @param {string} employeeId - Employee ID to remove
 * @returns {Promise<Array>} Updated employees array
 */
export const removeLaborBurdenEmployee = async (employeeId, userId = null) => {
  try {
    const auth = getAuth();
    const uid = userId || auth.currentUser?.uid;
    if (!uid) {
      throw new Error('User must be authenticated to remove labor burden employee');
    }

    // Ensure user document exists and get current data
    const userData = await ensureUserDocumentExists(uid);
    const currentLaborBurden = userData.laborBurden || { employees: [] };

    // Remove the employee
    const updatedEmployees = currentLaborBurden.employees.filter(emp => emp.id !== employeeId);

    // Save updated labor burden data
    await saveLaborBurdenData({
      ...currentLaborBurden,
      employees: updatedEmployees
    }, uid);

    return updatedEmployees;
  } catch (error) {
    console.error('Error removing labor burden employee:', error);
    throw error;
  }
}; 